import {
  deleteV1CompaniesCompanyIdMatchKeysId,
  getV1CompaniesCompanyIdMatchKeys,
  MatchKey_Read,
  postV1CompaniesCompanyIdMatchKeys,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { MatchKey } from './models';

type MatchableType = MatchKey_Read['matchable_type'];

interface FetchAllMatchKeysParams {
  companyId: string;
  matchableId?: string;
  matchableType?: MatchableType;
}

interface CreateMatchKeyParams {
  companyId: string;
  key: string;
  matchableId: string;
  matchableType: MatchableType;
}

interface DeleteMatchKeyParams {
  companyId: string;
  matchKeyId: string;
}

export default function useMatchKeys() {
  const fetchAllMatchKeys = useCallback(
    async ({ companyId, matchableId, matchableType }: FetchAllMatchKeysParams) => {
      try {
        return await fetchAllApiPages(
          (query) =>
            getV1CompaniesCompanyIdMatchKeys({
              path: { 'company-id': companyId },
              query: {
                ...(matchableType ? { matchable_type: matchableType } : {}),
                ...(matchableId ? { matchable_id: matchableId } : {}),
                ...query,
              },
            }),
          MatchKey.parse,
        );
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.match_keys.failed_to_fetch_match_keys'),
          );
        } catch {
          // Error already handled (toast + Sentry)
        }

        return [] as MatchKey[];
      }
    },
    [],
  );

  const createMatchKey = useCallback(
    async ({ companyId, key, matchableId, matchableType }: CreateMatchKeyParams) => {
      try {
        const response = await postV1CompaniesCompanyIdMatchKeys({
          path: { 'company-id': companyId },
          body: {
            key,
            matchable_id: matchableId,
            matchable_type: matchableType,
          },
        });

        return MatchKey.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.match_keys.failed_to_create_match_key'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteMatchKey = useCallback(
    async ({ companyId, matchKeyId }: DeleteMatchKeyParams) => {
      try {
        await deleteV1CompaniesCompanyIdMatchKeysId({
          path: { 'company-id': companyId, id: matchKeyId },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.match_keys.failed_to_delete_match_key'),
        );
        throw error;
      }
    },
    [],
  );

  return { createMatchKey, deleteMatchKey, fetchAllMatchKeys };
}
